import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, User, Settings } from "react-feather";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AddEdit_UserMaster = () => {
  const API_URL_SAVE = "UserMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/UserMaster/Id";
  const API_URL_USERTYPE = `${API_WEB_URLS.MASTER}/0/token/UserTypeMaster/Id/0`;
  
  const breadCrumbTitle = "User Master";
  const breadcrumbItem = "User Master";

  const [state, setState] = useState({
    id: 0,
    formData: { 
      FullName: "", 
      UserName: "",
      UserPass: "",
      F_UserType: "",
      Email: "",
    }
  });

  const [userTypeList, setUserTypeList] = useState([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const usernameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;

    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Fetch UserType list
  useEffect(() => {
    Fn_FillListData(dispatch, setUserTypeList, "gridData", API_URL_USERTYPE);
  }, [dispatch]);

  // Auto-focus on UserName field when component mounts
  useEffect(() => {
    if (usernameRef.current) {
      usernameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    FullName: Yup.string()
      .required("Full name is required")
      .min(2, "Full name must be at least 2 characters")
      .max(100, "Full name must be less than 100 characters"),
    UserName: Yup.string()
      .required("Username is required")
      .min(3, "Username must be at least 3 characters")
      .max(100, "Username must be less than 100 characters"),
    UserPass: state.id === 0 
      ? Yup.string()
          .required("Password is required")
          .min(6, "Password must be at least 6 characters")
          .max(100, "Password must be less than 100 characters")
      : Yup.string()
          .min(6, "Password must be at least 6 characters")
          .max(100, "Password must be less than 100 characters"),
    F_UserType: Yup.string()
      .required("User type is required"),
    Email: Yup.string()
      .required("Email is required")
      .email("Invalid email address")
      .max(100, "Email must be less than 100 characters"),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("FullName", values.FullName || "");
    formData.append("UserName", values.UserName || "");
    formData.append("UserPass", values.UserPass || "");
    formData.append("F_UserType", values.F_UserType || "");
    formData.append("Email", values.Email || "");
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "userid",
      navigate,
      "/UserMaster"
    );
  };

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header" style={{padding: '12px 20px'}}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={18} color="#6D68CB" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold">User Master</h5>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="master-form-card-body">
                <Formik 
                  initialValues={{
                    FullName: state.formData.FullName || "",
                    UserName: state.formData.UserName || "",
                    UserPass: "",
                    F_UserType: state.formData.F_UserType || "",
                    Email: state.formData.Email || "",
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="FullName" className="master-form-label">
                            <User size={16} className="me-2" />
                            Full Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              innerRef={usernameRef}
                              className={`master-form-input ${errors.FullName && touched.FullName ? 'is-invalid' : ''}`}
                              type="text"
                              name="FullName"
                              placeholder="Enter full name"
                            />
                            <div className="input-icon">
                              <User size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="FullName" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the complete name of the user
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="UserName" className="master-form-label">
                            <User size={16} className="me-2" />
                            Username <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.UserName && touched.UserName ? 'is-invalid' : ''}`}
                              type="text"
                              name="UserName"
                              placeholder="Enter username"
                            />
                            <div className="input-icon">
                              <User size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="UserName" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter a unique username for login
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="UserPass" className="master-form-label">
                            <User size={16} className="me-2" />
                            Password {state.id === 0 && <span className="required">*</span>}
                            {state.id > 0 && <span className="text-muted">(Leave blank to keep current)</span>}
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.UserPass && touched.UserPass ? 'is-invalid' : ''}`}
                              type="password"
                              name="UserPass"
                              placeholder={state.id === 0 ? "Enter password" : "Enter new password"}
                            />
                            <div className="input-icon">
                              <User size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="UserPass" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Minimum 6 characters required
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_UserType" className="master-form-label">
                            <User size={16} className="me-2" />
                            User Type <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_UserType && touched.F_UserType ? 'is-invalid' : ''}`}
                              name="F_UserType"
                            >
                              <option value="">Select User Type</option>
                              {userTypeList && userTypeList.map((userType) => (
                                <option key={userType.Id} value={userType.Id}>
                                  {userType.Name}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_UserType" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select the type of user
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Email" className="master-form-label">
                            <User size={16} className="me-2" />
                            Email <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Email && touched.Email ? 'is-invalid' : ''}`}
                              type="email"
                              name="Email"
                              placeholder="Enter email address"
                            />
                            <div className="input-icon">
                              <User size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Email" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter a valid email address
                          </div>
                        </div>
                      </div>

                      <div className="master-form-actions mt-3">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate("/UserMaster")}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            {state.id > 0 ? 'Update User' : 'Save User'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_UserMaster;

